import { Injectable } from '@angular/core';
import { NetworkHelperService } from './network-helper.service';
import { environment } from '../../../environments/environment';
import { SuccessResponse } from '../models/sucess-response';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  IBodyResendCode,
  IBodyQuestionary,
  IParamsGetCompanies,
  IParamsGetEstimations,
  IBodyInterestPromotion,
  IBodyVerifyPhoneNumber,
  IBodyGetRoofSize,
  LeadVertical,
} from '../../modules/questionnaire/models/questionary';
import { RoofProblemOption } from '../../modules/questionnaire/models/roof-problem';
import { Company } from '../../modules/questionnaire/models/company';
import { Promotion } from '../../modules/questionnaire/models/promotion';
import { StorageService } from './storage.service';
import { RoofMaterialOption } from '../../modules/questionnaire/models/roofing-materials';
import { RoofSizeResponse } from 'src/app/modules/questionnaire/models/roof-size';
import { Pricing } from 'src/app/modules/questionnaire/models/pricing';

@Injectable({
  providedIn: 'root'
})
export class QuestionnaireService {
  private _questionaryBody: IBodyQuestionary;
  private questionaryBodySubject: BehaviorSubject<IBodyQuestionary>;
  public questionaryBody$: Observable<IBodyQuestionary>;

  constructor(private storageService: StorageService, private networkHelperService: NetworkHelperService) {
    const storedBody = this.storageService.getItem('session');
    this._questionaryBody = storedBody ? JSON.parse(storedBody) : {} as IBodyQuestionary;
    this.questionaryBodySubject = new BehaviorSubject<IBodyQuestionary>(this._questionaryBody);
    this.questionaryBody$ = this.questionaryBodySubject.asObservable();
  }

  public updateQuestionaryStep(stepData: Partial<IBodyQuestionary>): void {
    this._questionaryBody = { ...this._questionaryBody, ...stepData };
    this.questionaryBodySubject.next(this._questionaryBody);
    this.storageService.setItem('session', JSON.stringify(this._questionaryBody));
  }

  public clearQuestionary(): void {
    this._questionaryBody = {
      uuid: this._questionaryBody?.uuid
    } as IBodyQuestionary;
    this.storageService.removeItem('session');
    this.storageService.removeItem('originLeads');
    // Clear with origin flags so a later /questionnaire visit from Home doesn't
    // inherit a stale siding/NRC landing path and the wrong slide order.
    this.storageService.removeItem('landing-page-path');
    this.questionaryBodySubject.next(this._questionaryBody);
  }

  getRoofDamageTypes(vertical?: LeadVertical) {
    return this.networkHelperService.get<RoofProblemOption[]>(
      `${environment.apiUrl}/user/roof/damage/types`,
      vertical ? { vertical } : {}
    );
  }

  getMaterials(vertical?: LeadVertical) {
    return this.networkHelperService.get<RoofMaterialOption[]>(
      `${environment.apiUrl}/user/materials`,
      vertical ? { vertical } : {}
    );
  }

  getBallparkPricingByState(state: string) {
    return this.networkHelperService.get<Pricing>(
      `${environment.apiUrl}/ballpark/state/${state}`,
      {}
    );
  }

  getRoofSize(body: IBodyGetRoofSize) {
    return this.networkHelperService.post<RoofSizeResponse>(
      `${environment.apiUrl}/fetch-roof-size`,
      body
    );
  }

  addQuestionary(body: IBodyQuestionary) {
    return this.networkHelperService.post<string>(
      `${environment.apiUrl}/user/questionary`, body
    );
  }

  verifyPhoneNumber(body: IBodyVerifyPhoneNumber) {
    return this.networkHelperService.post<SuccessResponse>(
      `${environment.apiUrl}/user/verify/phoneNumber`, body
    );
  }

  resendCode(body: IBodyResendCode) {
    return this.networkHelperService.post<SuccessResponse>(
      `${environment.apiUrl}/user/resend/code`, body
    );
  }

  getCompanies(params: IParamsGetCompanies) {
    return this.networkHelperService.get<Company[]>(
      `${environment.apiUrl}/user/companies`, params
    );
  }

  getEstimations(params: IParamsGetEstimations) {
    return this.networkHelperService.get<Company[]>(
      `${environment.apiUrl}/user/companies/estimations`, params
    );
  }

  getCompany(uuid: string) {
    return this.networkHelperService.get<Company>(
      `${environment.apiUrl}/user/company/${uuid}`, {}
    );
  }

  getCompanyPromotions(uuid: string) {
    return this.networkHelperService.get<Promotion[]>(
      `${environment.apiUrl}/user/company/promotions/${uuid}`, {}
    );
  }

  /**
   * `GET /user/companies` (and estimations) often returns `promotion: []`. The card back reads
   * `company.promotion`; merge rows from `GET /user/company/promotions/:companyUuid` when present.
   */
  enrichCompaniesWithPromotions(companies: Company[]): Observable<Company[]> {
    if (!companies?.length) {
      return of(companies);
    }
    return forkJoin(
      companies.map(c =>
        this.getCompanyPromotions(c.uuid).pipe(catchError(() => of([] as Promotion[]))),
      ),
    ).pipe(
      map(promoLists => {
        companies.forEach((c, i) => {
          const fetched = promoLists[i];
          if (fetched?.length) {
            c.promotion = fetched;
          }
        });
        return companies;
      }),
    );
  }

  interestPromotion(id: number, body: IBodyInterestPromotion) {
    return this.networkHelperService.post<SuccessResponse>(
      `${environment.apiUrl}/user/promotion/${id}/interest`, body
    );
  }
}
